import {
  ChatCreateRouteAction,
  ChatProposal,
  ChatRouteStartLocation,
} from "@mapmycustomers/shared/types/chat/ChatContent";
import { FieldAgentAcceptedProposal } from "@mapmycustomers/shared/types/chat/FieldAgentChat";
import GeoPoint from "@mapmycustomers/shared/types/shapes/GeoPoint";
import { isRecord, ROUTE_STOP_ENTITY_TYPES } from "@mapmycustomers/shared/util/chat/chatWireGuards";
import { parseGeoPoint } from "@mapmycustomers/shared/util/chat/parseCreateRouteAction";

import { CreateRouteStopKey, normalizeCreateRouteStopOrder } from "./createRouteStops";
import { sameInstant } from "./logActivityDraft";

// A location as the card holds it. The point is null for a picked address the geocode couldn't place
// on the map — the platform does not geocode, so that one blocks Create route rather than travels.
export interface CreateRouteLocation {
  address: string;
  geoPoint: GeoPoint | null;
}

// The draft's keys. An absent key is the agent's value; `order` is every stop in the rep's order,
// present once they moved one.
export interface CreateRouteDraft {
  endLocation?: CreateRouteLocation | null;
  order?: CreateRouteStopKey[];
  routeName?: string;
  startAt?: string;
  startLocation?: CreateRouteLocation | null;
}

export interface CreateRouteValues {
  endLocation: CreateRouteLocation | null;
  routeName: string;
  startAt: string;
  startLocation: CreateRouteLocation | null;
}

// finalValues has no key allowlist on the platform's side we can lean on: these are the keys
// create_route accepts, declared once.
interface CreateRouteFinalValues {
  endLocation?: ChatRouteStartLocation;
  routeName?: string;
  startAt?: string;
  startLocation?: ChatRouteStartLocation | null;
  stopOrder?: CreateRouteStopKey[];
}

// A stored draft outlives a reload, so each entry is re-checked rather than trusted.
const readLocation = (value: unknown): CreateRouteLocation | null | undefined => {
  if (value === null) {
    return null;
  }
  if (!isRecord(value) || typeof value.address !== "string") {
    return undefined;
  }
  // A blank address is no location, whatever point it carries.
  return value.address.trim()
    ? { address: value.address, geoPoint: parseGeoPoint(value.geoPoint) ?? null }
    : null;
};

const readOrder = (value: unknown): CreateRouteStopKey[] | undefined =>
  Array.isArray(value)
    ? value.filter(
        (stop): stop is CreateRouteStopKey =>
          isRecord(stop) &&
          typeof stop.entityId === "number" &&
          ROUTE_STOP_ENTITY_TYPES.some((type) => type === stop.entityType)
      )
    : undefined;

export const readCreateRouteDraft = (
  draft: Record<string, unknown> | undefined
): CreateRouteDraft => ({
  endLocation: readLocation(draft?.endLocation),
  order: readOrder(draft?.order),
  routeName: typeof draft?.routeName === "string" ? draft.routeName : undefined,
  startAt: typeof draft?.startAt === "string" ? draft.startAt : undefined,
  startLocation: readLocation(draft?.startLocation),
});

const isSameLocation = (
  left: CreateRouteLocation | null,
  right: ChatRouteStartLocation | CreateRouteLocation | null
) =>
  left?.address === right?.address &&
  left?.geoPoint?.coordinates.join() === right?.geoPoint?.coordinates.join();

// Only what differs from the proposal, in the draft's own shape. Empty = the proposal as sent.
export const readCreateRouteChanges = (
  action: ChatCreateRouteAction,
  draft: Record<string, unknown> | undefined
): CreateRouteDraft => {
  const { endLocation, order, routeName, startAt, startLocation } = readCreateRouteDraft(draft);
  const trimmedName = routeName?.trim();
  const stopOrder = normalizeCreateRouteStopOrder(action.stops, order);
  return {
    ...(endLocation ? { endLocation } : {}),
    ...(stopOrder ? { order: stopOrder } : {}),
    // Echoing the proposed name would pin it, so a date name would stop following the start day.
    ...(trimmedName !== undefined && trimmedName !== action.routeName
      ? { routeName: trimmedName }
      : {}),
    // Compared as instants: the same start written with another offset is no edit.
    ...(startAt !== undefined && !sameInstant(startAt, action.proposedValues.startAt)
      ? { startAt }
      : {}),
    ...(startLocation !== undefined &&
    !isSameLocation(startLocation, action.proposedValues.startLocation)
      ? { startLocation }
      : {}),
  };
};

export const isCreateRouteDraftEqual = (
  action: ChatCreateRouteAction,
  draft: Record<string, unknown>
): boolean => !Object.keys(readCreateRouteChanges(action, draft)).length;

// What the card shows: the rep's value where they set one, else the agent's.
export const resolveCreateRouteValues = (
  action: ChatCreateRouteAction,
  draft: Record<string, unknown> | undefined
): CreateRouteValues => {
  const values = readCreateRouteDraft(draft);
  return {
    endLocation: values.endLocation ?? null,
    routeName: values.routeName ?? action.routeName,
    startAt: values.startAt ?? action.proposedValues.startAt,
    startLocation:
      values.startLocation === undefined
        ? action.proposedValues.startLocation
        : values.startLocation,
  };
};

// A location the map can't place never travels; it holds Create route instead.
const toWireLocation = (location: CreateRouteLocation): ChatRouteStartLocation | undefined =>
  location.geoPoint ? { address: location.address, geoPoint: location.geoPoint } : undefined;

const toCreateRouteFinalValues = (
  action: ChatCreateRouteAction,
  draft: Record<string, unknown> | undefined
): CreateRouteFinalValues | undefined => {
  const { endLocation, order, routeName, startAt, startLocation } = readCreateRouteChanges(
    action,
    draft
  );
  const finalValues: CreateRouteFinalValues = {
    ...(endLocation && toWireLocation(endLocation)
      ? { endLocation: toWireLocation(endLocation) }
      : {}),
    ...(routeName !== undefined ? { routeName } : {}),
    ...(startAt !== undefined ? { startAt } : {}),
    ...(startLocation === null ? { startLocation: null } : {}),
    ...(startLocation && toWireLocation(startLocation)
      ? { startLocation: toWireLocation(startLocation) }
      : {}),
    // Once the rep moved a stop the route keeps their order, even if it ends up as proposed.
    ...(order ? { stopOrder: order } : {}),
  };
  return Object.keys(finalValues).length ? finalValues : undefined;
};

export const toCreateRouteAcceptedItem = (
  proposal: ChatProposal,
  action: ChatCreateRouteAction,
  draft: Record<string, unknown> | undefined
): FieldAgentAcceptedProposal => {
  const finalValues = toCreateRouteFinalValues(action, draft);
  return finalValues
    ? { finalValues: { ...finalValues }, proposalId: proposal.proposalId }
    : { proposalId: proposal.proposalId };
};
